import React, { createContext, useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectUserFormData } from "app/redux/selector";
import { socket } from "socket";
const SocketContext = createContext(socket);

enum ENUM_USER_STATUS {
  ONLINE = "online",
  OFFLINE = "offline",
  NOT_ACTIVE = "not_active",
}

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  const { _id } = useSelector(selectUserFormData);

  const activityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanupRef = useRef(false); // ✅ StrictMode guard

  useEffect(() => {
    if (!_id) return;

    cleanupRef.current = false; // ✅ Reset on each real mount

    // ── Set userId BEFORE connecting ───────────────────
    socket.io.opts.query = { userId: _id };

    if (socket.connected) {
      socket.disconnect();
    }

    // ✅ Small delay to let StrictMode's first unmount fire before connecting
    const connectTimer = setTimeout(() => {
      if (cleanupRef.current) return; // ✅ Abort if already unmounted
      socket.connect();
    }, 100);

    // ── Connection Events ──────────────────────────────
    const onConnect = () => {
      console.log("✅ Socket connected:", socket.id, "| userId:", _id);
    };

    const onDisconnect = (reason: string) => {
      console.warn("⚠️ Socket disconnected:", reason);
      if (reason === "io server disconnect" && !cleanupRef.current) {
        socket.connect();
      }
    };

    const onConnectError = (err: Error) => {
      console.error("❌ Connection error:", err.message);
    };

    const onUserStatus = (data: {
      userId: string;
      status: ENUM_USER_STATUS;
      lastSeen?: string;
      reason?: string;
    }) => {
      // console.log(`📡 Status update: ${data.userId} → ${data.status}`);
    };

    // ── Throttled Activity Handler ─────────────────────
    const handleActivity = () => {
      if (!socket.connected || activityTimerRef.current) return;
      socket.emit("activity");
      activityTimerRef.current = setTimeout(() => {
        activityTimerRef.current = null;
      }, 3000);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("userStatus", onUserStatus);

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);

    // ── Cleanup ────────────────────────────────────────
    return () => {
      cleanupRef.current = true; // ✅ Signal that we're cleaning up
      clearTimeout(connectTimer); // ✅ Cancel connect if not fired yet

      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("userStatus", onUserStatus);

      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);

      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
        activityTimerRef.current = null;
      }

      // ✅ Only fully disconnect on real unmount, not StrictMode's fake one
      // Use a small delay — if remounted quickly, cancel the disconnect
      setTimeout(() => {
        if (cleanupRef.current) {
          socket.disconnect();
        }
      }, 200);
    };
  }, [_id]);

  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
